import { memo, useCallback, useMemo } from "react";
import { FloatInput } from "@components/Inputs/FloatInput";
import { SurveyDetailsSampleProvider, SurveyDetailsSampleProviderExtended } from "@core/api/useSurveys";
import ProviderLinkInput from "@pages/SurveyDetails/components/ProviderLinkInput";
import { copyToClipboard, formatNumber } from "@core/utils/string";
import { toast } from "react-toastify";
import Button from "@components/Button";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const onDisabledInputChange = () => undefined;

const ProviderColumn = ({
    provider,
    initialProvider,
    index,
    onChange,
}: {
    provider: SurveyDetailsSampleProvider,
    initialProvider: SurveyDetailsSampleProviderExtended,
    index: number,
    onChange: <T extends keyof SurveyDetailsSampleProvider>(
        idx: number,
        field: T,
        value: SurveyDetailsSampleProvider[T]
    ) => void
}) => {
    const percentageValue = useMemo(() => {
        // Transform 0.550000000001 to 55
        const percentage = ((provider.percentage ?? 0) * 100);
        return formatNumber(percentage);
    }, [provider.percentage]);

    const changeToPerentageValue = useCallback((value: string) => {
        // Transform 55.0000000001 to 0.55
        const fixedValue = +formatNumber(+value) / 100;
        onChange(index, 'percentage', fixedValue);
    }, [onChange, index]);

    return (
        <div className="SurveyDetails__provider-column">
            <div className="SurveyDetails__provider-title">
                <span>{initialProvider.name ?? `Provider ${index + 1}`}:</span>
                <div className="SurveyDetails__param-value text-input left-align">
                    {/* percentage is a number between 0 and 1, we need to display it as a percentage thus we transform it to a number between 0 and 100 */}
                    <FloatInput
                        value={percentageValue}
                        setValue={changeToPerentageValue}
                        isSmall
                    />
                    <span>&nbsp;%</span>
                </div>
            </div>
            <div className="SurveyDetails__form-item-row">
                <FloatInput
                    value={initialProvider.cmxId ?? ''}
                    setValue={onDisabledInputChange}
                    isSmall
                    isDisabled
                />
                <Button
                    modifiers={['naked']}
                    className="SurveyDetails__copy-button"
                    onClick={() => {
                        copyToClipboard(initialProvider.cmxId ?? '');
                        toast.success(`${initialProvider.name} CMIX ID copied!`);
                    }}
                >
                    <ContentCopyIcon />
                </Button>
            </div>
            <hr className="SurveyDetails__divider" />
            <div className="SurveyDetails__form-item-group">
                <ProviderLinkInput
                    label="Complete"
                    value={provider.completeLink ?? ''}
                    onChange={(value) => onChange(index, 'completeLink', value)}
                />
                <ProviderLinkInput
                    label="Terminate"
                    value={provider.terminateLink ?? ''}
                    onChange={(value) => onChange(index, 'terminateLink', value)}
                />
                <ProviderLinkInput
                    label="Overquota"
                    value={provider.overquotaLink ?? ''}
                    onChange={(value) => onChange(index, 'overquotaLink', value)}
                />
                <ProviderLinkInput
                    label="Quality Terminate"
                    value={provider.qualityLink ?? ''}
                    onChange={(value) => onChange(index, 'qualityLink', value)}
                />
            </div>
        </div>
    )
};

export default memo(ProviderColumn);